/**
 * AdminSpacCleanupButton — Round P121 (Feb 2026).
 *
 * One-time retroactive sweep for blank-check / SPAC misclassification.
 *
 * Background: the `ipo_autoclassify` cron previously misclassified
 * blank-check SPACs as BDCs — SIC 6770 was wrongly lumped into the
 * BDC set, and the "capital corp" name matcher caught SPAC names like
 * "Churchill Capital Corp XII". Worse, those rows were $setOnInsert'd
 * with `pays_dividend: True`, bypassing the 5-point dividend check
 * and polluting the dividend universe with cash shells that pay
 * nothing.
 *
 * The forward-looking classifier fix is in place (see ipo_warmer.py +
 * server.py `_classify_ipo_security_type`). This button runs a
 * one-time retroactive sweep to fix ALREADY-POLLUTED rows in
 * stock_cache: flags flip to `is_spac=True, is_bdc=False,
 * pays_dividend=False`, and the row drops out of the dividend
 * universe automatically. Safe to re-run — idempotent (second run
 * flips 0 rows and reports how many were already correct).
 *
 * Security (defense in depth):
 *   Layer 1: Component self-gates on `user.role !== 'admin'`.
 *   Layer 2: Backend `POST /api/admin/spac-cleanup` gates on role.
 *   Layer 3: Rendered inside Dashboard's admin group which itself
 *            is admin-only.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wand2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSpacCleanupButton() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (user?.role !== 'admin') return null;

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/spac-cleanup`,
        {},
        { withCredentials: true },
      );
      const sample = (data.changed_tickers_sample || []).slice(0, 8).join(', ');
      toast.success(`SPAC cleanup complete`, {
        description: `Scanned ${data.scanned}. Flipped ${data.flipped}. Already correct: ${data.already_correct}.${data.etf_reverted?.length ? ` ETFs restored: ${data.etf_reverted.join(', ')}.` : ''}${sample ? ` Sample: ${sample}` : ''}`,
        duration: 9000,
      });
    } catch (err) {
      toast.error('SPAC cleanup failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-slate-600 hover:bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      data-testid="admin-spac-cleanup-btn"
      title="Retroactive sweep: find every stock_cache row that looks like a SPAC (SIC 6770, 'BLANK CHECKS' industry, 'blank check company' in description, or SPAC-unit ticker patterns) and flip its flags to is_spac=True, is_bdc=False, pays_dividend=False. Idempotent — safe to re-run."
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
      SPAC Cleanup
    </button>
  );
}
